import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faDisplay } from '@fortawesome/free-solid-svg-icons';
import { useCustomerTheme } from '@/providers/CustomerThemeProvider';
import type { CustomerThemeMode } from '@/providers/CustomerThemeProvider';

const OPTIONS: Array<{ mode: CustomerThemeMode; label: string; icon: typeof faSun }> = [
  { mode: 'light', label: 'Light', icon: faSun },
  { mode: 'dark', label: 'Dark', icon: faMoon },
  { mode: 'system', label: 'System', icon: faDisplay },
];

/**
 * Three-way segmented Light / Dark / System control. Lives in the header's
 * user menu (not a standalone header icon) per UI_UX_STANDARDS.md's
 * "minimal visual noise" header guidance, and on the Appearance page.
 *
 * D-130 Phase 6B — labelled "Theme mode", not "Theme": the Appearance page
 * shows this control beside the six-skin picker, and two radiogroups both
 * named "Theme" would be ambiguous to a screen reader. Mode and skin are
 * orthogonal axes.
 */
export function CustomerThemeToggle() {
  const { mode, setMode } = useCustomerTheme();

  return (
    <div
      role="radiogroup"
      aria-label="Theme mode"
      className="flex items-center gap-0.5 rounded-lg border border-customer-border bg-customer-bg p-0.5"
    >
      {OPTIONS.map((opt) => {
        const active = mode === opt.mode;
        return (
          <button
            key={opt.mode}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={opt.label}
            title={opt.label}
            onClick={() => setMode(opt.mode)}
            className={[
              // 6R: was h-8/min-w-8 (32px) — under the 44px minimum at every width.
              'flex h-11 min-w-11 flex-1 items-center justify-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring focus-visible:ring-offset-1 focus-visible:ring-offset-customer-bg',
              active
                ? 'bg-customer-surface text-customer-primary-text shadow-sm'
                : 'text-customer-text-secondary hover:text-customer-text',
            ].join(' ')}
          >
            <FontAwesomeIcon icon={opt.icon} className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
